import { axiosClient } from '../../../library/Service/axiosClient';
import PageConfigBusinessDirectory from './PageConfigBusinessDirectory.json';

class BusinessDirectoryService {
    static pageLoader(params: any): Promise<any> {
        const state: any = {};
        const searchCriteria = this.getDefaultSearchCriteria();

        return Promise.all([
            this.getPageConfig(),
            this.search(searchCriteria),
            this.getDynamicFilterDomains()
        ]).then(([formconfig, providerResponse, filterDomains]) => {
            const payload = providerResponse?.data ?? {};
            state.formConfig = formconfig;
            state.data = {
                providerList: payload?.data ?? [],
                pagination: {
                    total: payload?.total ?? 0,
                    page: payload?.page ?? searchCriteria.page,
                    limit: payload?.limit ?? searchCriteria.limit,
                },
                hasMore: (payload?.page ?? searchCriteria.page) * (payload?.limit ?? searchCriteria.limit) < (payload?.total ?? 0),
                searchCriteria,
                filterDomains
            };
            state.routeInfo = params;
            state.internal = { gridState: {} };

            return Promise.resolve(state);
        }).catch((error) => {
            console.error('Error loading Business Directory:', error);
            return Promise.resolve({
                formConfig: PageConfigBusinessDirectory,
                data: {
                    providerList: [],
                    pagination: { total: 0, page: 1, limit: 20 },
                    hasMore: false,
                    searchCriteria,
                    filterDomains: {}
                },
                routeInfo: params,
                internal: { gridState: {} }
            });
        });
    }

    static getPageConfig() {
        return Promise.resolve(PageConfigBusinessDirectory);
    }

    static async getDynamicFilterDomains() {
        try {
            const response = await axiosClient().get(
                `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/report-filters`
            );
            return response?.data || {};
        } catch (error) {
            console.error('Failed to fetch provider filter domains:', error);
            return {};
        }
    }

    static getDefaultSearchCriteria(): any {
        return {
            fuzzySearchText: '',
            institutes: null,
            category: [],
            countryCode: '',
            cityCode: '',
            availability: '',
            experience_min: null,
            experience_max: null,
            page: 1,
            limit: 20,
            directoryType: 'approved'
        };
    }

    static search(searchCriteria: any) {
        const criteria = this.sanitizeCriteria(searchCriteria);
        return axiosClient().post(`${process.env.REACT_APP_REPORTS_SERVER}/business-directory/search-provider-report`, criteria);
    }

    static exportCSV(searchCriteria: any) {
        const criteria = this.sanitizeCriteria(searchCriteria);
        // Remove pagination — backend exports full filtered dataset
        const { page, limit, ...exportCriteria } = criteria;

        return axiosClient().get(
            `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/export`,
            {
                params: exportCriteria,
                responseType: 'blob'
            }
        ).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `providers_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        });
    }

    static searchAlumni(term: string, instituteId?: string) {
        let url = `${process.env.REACT_APP_REPORTS_SERVER}/common/list-for-name-search?term=${encodeURIComponent(term)}`;
        if (instituteId) url += `&instituteId=${encodeURIComponent(instituteId)}`;
        return axiosClient().get(url);
    }

    static createProvider(payload: any) {
        return axiosClient().post(`${process.env.REACT_APP_COMMON_API}/providers`, payload);
    }

    static getProviderDetail(providerId: string) {
        return axiosClient().get(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}`);
    }

    static trackProviderView(serviceProviderId: string) {
        return axiosClient().post(`${process.env.REACT_APP_COMMON_API}/track-provider-view`, {
            serviceProviderId,
        });
    }

    static updateProvider(providerId: string, payload: any) {
        return axiosClient().put(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}`, payload);
    }

    static markInactive(providerId: string) {
        return axiosClient().delete(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}`);
    }

    static removeProvider(providerId: string) {
        return axiosClient().delete(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}/remove`);
    }

    static sanitizeCriteria(searchCriteria: any) {
        const criteria: any = {};

        const scopedInstitutes = Array.isArray(searchCriteria?.institutes)
            ? searchCriteria.institutes.filter(Boolean)
            : searchCriteria?.institutes
                ? [searchCriteria.institutes]
                : [];

        // Handle institutes
        if (scopedInstitutes.length > 0) {
            criteria.institutes = scopedInstitutes;
        }

        // Handle category array
        if (searchCriteria.category && Array.isArray(searchCriteria.category)) {
            criteria.category = searchCriteria.category.map((item: any) =>
                typeof item === 'object' ? Object.values(item)[0] : item
            ).filter(Boolean);
        }

        // Handle location string
        if (searchCriteria.countryCode) {
            criteria.countryCode = searchCriteria.countryCode;
        }

        if (searchCriteria.cityCode) {
            criteria.cityCode = searchCriteria.cityCode;
        }

        if (searchCriteria.availability) {
            const availabilityValue = Array.isArray(searchCriteria.availability)
                ? searchCriteria.availability[0]
                : searchCriteria.availability;

            criteria.availability = typeof availabilityValue === 'object'
                ? Object.values(availabilityValue)[0]
                : availabilityValue;
        }

        if (searchCriteria.experience && Array.isArray(searchCriteria.experience) && searchCriteria.experience.length === 2) {
            criteria.experience_min = Number(searchCriteria.experience[0]);
            criteria.experience_max = Number(searchCriteria.experience[1]);
        }

        if (typeof searchCriteria.experience === 'string' && searchCriteria.experience.includes('-')) {
            const [min, max] = searchCriteria.experience.split('-').map((val: string) => Number(val.trim()));
            if (Number.isFinite(min)) criteria.experience_min = min;
            if (Number.isFinite(max)) criteria.experience_max = max;
        }

        if (searchCriteria.experience_min !== undefined && searchCriteria.experience_min !== null && searchCriteria.experience_min !== '') {
            criteria.experience_min = Number(searchCriteria.experience_min);
        }

        if (searchCriteria.experience_max !== undefined && searchCriteria.experience_max !== null && searchCriteria.experience_max !== '') {
            criteria.experience_max = Number(searchCriteria.experience_max);
        }

        // Handle pagination
        if (searchCriteria.page) {
            criteria.page = searchCriteria.page;
        }

        if (searchCriteria.limit) {
            criteria.limit = searchCriteria.limit;
        }

        if (searchCriteria.directoryType) {
            criteria.directoryType = searchCriteria.directoryType;
        }

        if (searchCriteria.fuzzySearchText !== undefined && searchCriteria.fuzzySearchText !== null) {
            criteria.fuzzySearchText = String(searchCriteria.fuzzySearchText).trim();
        }

        return criteria;
    }
}

export default BusinessDirectoryService;
